import React, { useCallback, useMemo, useState } from 'react';
import { SourceItem } from "../../shared/models/source-item.model";
import { useSiteContext } from "../../state/SiteContext";
import { FilterActionName, useFilterAction, useFilterState, useSystemPropertyFilterStringValue } from "../../state/FilterContext";
import { TextExtractor, findFirstComponentOfType } from "../internal/TextExtractor";
import { ArrayProvider } from "../../state/ArrayContext";
import { BaseSelector, collectProps } from "../internal/BaseSelector";
import { ValidityValuesQuery } from '../source-components/array-sources/ValidityValuesQuery';
import { BaselinePropertyFilterValues } from '../source-components/array-sources/BaselinePropertyFilterValues';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

interface BaselineSelectorProps {
  optionlabeltemplate: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  children: React.ReactNode,
}
/***
 * A selector component that allows the user to select a value from a list of values.
 * The list of values is loaded using an ArraySource specification.
 */
export const BaselineSelector = React.memo(function BaselineSelector({ optionlabeltemplate, redirect, redirectLayout = "", activateToggleSwitch, children }: BaselineSelectorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const removeSystemProperty = useFilterAction(FilterActionName.REMOVE_SYSTEM_PROPERTY);
  const updateBaseline = useFilterAction(FilterActionName.UPDATE_BASELINE);
  const hidFilter = useSystemPropertyFilterStringValue("hid");
  const filterState = useFilterState();
  const toggleSwitchController = useToggleSwitchController();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState("");

  // Get the current validity path from the validity child
  const validityChild = useMemo(() => findFirstComponentOfType(children, "arg-validity"), [children]);
  const [validityPath, setValidityPath] = useState<string | null>(null);

  const { siteState } = useSiteContext();

  // Get the valueset array from the valueset child
  const [valueSetArray, setValueSetArray] = useState<Array<SourceItem>>([]);
  const valuesetChild = useMemo(() => findFirstComponentOfType(children, "arg-valueset"), [children]);

  // Get the initial values array from a ValidityPropertyFilterValues array source defined in the component output
  const [initialValuesArray, setInitialValuesArray] = useState<Array<SourceItem>>([]);

  // Get the autoCompleteProps from the autoCompleteProps child
  const autoCompletePropsChild = useMemo(() => findFirstComponentOfType(children, "arg-autocompleteprops"), [children]);
  const autoCompleteProps = useMemo(() => collectProps(autoCompletePropsChild), [autoCompletePropsChild]);

  // Get the textfieldProps from the textfieldProps child
  const textfieldPropsChild = useMemo(() => findFirstComponentOfType(children, "arg-textfieldprops"), [children]);
  const textfieldProps = useMemo(() => collectProps(textfieldPropsChild), [textfieldPropsChild]);

  const setArray = useCallback((array: Array<SourceItem>) => {
    setInitialValuesArray(array);
  }, [setInitialValuesArray]);

  const onChangeFunc = useCallback((values: Array<SourceItem>) => {
    const newBaseline = values.length ? values[0].props?.info?.path[0].toString() || values[0].value.toString() : validityPath;
    const intentsRedirectNavigate = hasFilterRedirectNavigateIntent(redirect, redirectLayout);
    let redirectToPath: string | null = null;
    if (intentsRedirectNavigate) {
      const filterStateCopy = { ...filterState };
      if (hidFilter) {
        delete filterStateCopy.systemProperties?.["hid"];
      }
      filterStateCopy.baseline = newBaseline as string || undefined;
      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState?.site);
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      redirectToPath = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
    }
    if (hidFilter) {
      removeSystemProperty("hid");
    }
    updateBaseline(newBaseline);
    activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "BaselineSelector");
    if (redirectToPath) {
      navigate(redirectToPath, { replace: true });
    }
  }, [
    activateToggleSwitch,
    removeSystemProperty,
    updateBaseline,
    navigate,
    redirect,
    redirectLayout,
    filterState,
    siteState.sitePath,
    siteState.site,
    hidFilter,
    validityPath,
    location.hash,
    location.pathname,
    toggleSwitchController,
  ]);

  const memoizedInitialStringValues = useMemo(() => {
    return initialValuesArray.map(item => item.value.toString());
  }, [initialValuesArray]);

  return <>
    <BaseSelector
      label={labelValue}
      valueset={valueSetArray}
      initialValues={memoizedInitialStringValues}
      optionlabeltemplate={optionlabeltemplate}
      autocompleteprops={autoCompleteProps}
      textfieldprops={textfieldProps}
      multiple={false}
      onChange={onChangeFunc}
    />

    <ArrayProvider setArray={setValueSetArray}>
      {valuesetChild?.props.children || <ValidityValuesQuery validityPathArg={validityPath} />}
    </ArrayProvider>
    <ArrayProvider setArray={setArray}>
      <BaselinePropertyFilterValues parentBaselineProp={validityPath} />
    </ArrayProvider>
    <TextExtractor onExtractedText={setLabelValue}>
      {labelChild?.props.children}
    </TextExtractor>
    <TextExtractor onExtractedText={setValidityPath}>
      {validityChild?.props.children}
    </TextExtractor>
  </>
});